import React from 'react'
import { ChevronRight } from 'lucide-react'
import { SvgIcon } from '../svg-icon'
import { FINDING_CATALOGUE, REVIEW_FINDING_IDS, RISKS_BY_FINDING } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { SeverityPill } from './status'

/**
 * Everything the scan looks for, browsable.
 *
 * A reader who cannot find out what was checked has to take a page of green ticks on trust. Alphabetical,
 * because this is a list to look a code up in, and each entry opens onto how that check decides.
 */
export function Catalogue() {
  return (
    <ul className="divide-y divide-border">
      {[...FINDING_CATALOGUE]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((entry) => (
          <li key={entry.id}>
            <details className="group/check">
              <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 py-1.5 hover:bg-muted/50">
                <SvgIcon
                  icon={ChevronRight}
                  size={12}
                  className="shrink-0 text-muted-foreground transition-transform group-open/check:rotate-90"
                />
                {/* "Can ask you to confirm" is what the catalogue means: whether it did is the widget's verdict. */}
                <SeverityPill tone={REVIEW_FINDING_IDS.includes(entry.id) ? 'review' : entry.severity} />
                <code className="font-mono text-xs text-primary">{entry.id}</code>
                {/*
                 * Titles are verb phrases about the page, so the catalogue can state the rule by prefixing
                 * them once. In a report the bare title is right — there it describes a page that did this.
                 */}
                <span>
                  <span className="text-muted-foreground">Reports when the page </span>
                  <span className="font-medium">{lowerFirst(entry.title)}</span>
                </span>
                <span className="ml-auto shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {RISKS_BY_FINDING[entry.id]?.map((risk) => risk.label).join(', ') ?? 'no risk category'}
                </span>
              </summary>
              <dl className="pb-3 pl-6 text-xs leading-relaxed">
                <dt className="font-semibold text-foreground">Why it matters</dt>
                <dd className="mb-2 text-muted-foreground">{entry.why}</dd>
                <dt className="font-semibold text-foreground">How it is detected</dt>
                <dd className="mb-2 text-muted-foreground">{entry.detection}</dd>
                {entry.limits && (
                  <>
                    <dt className="font-semibold text-foreground">What it cannot see</dt>
                    <dd className="text-muted-foreground">{entry.limits}</dd>
                  </>
                )}
              </dl>
            </details>
          </li>
        ))}
    </ul>
  )
}

/** The catalogue entry behind a code: its declared severity, and the prose that says what the check looks for. */
export function catalogueEntry(id: string) {
  return FINDING_CATALOGUE.find((entry) => entry.id === id)
}

/** "Calls the backend inside a loop" after "Reports when the page ". Only the first letter; acronyms keep their case. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}
